import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Inbox, Trash2, MailOpen, Mail, Reply, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Seo } from "../components/Seo";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmt = (d) => new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function AdminMessages() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [msgs, setMsgs] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setFetching(true);
    try { const { data } = await http.get("/contact/messages"); setMsgs(data); }
    catch { toast.error("Could not load messages."); }
    finally { setFetching(false); }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { state: { from: "/admin/messages" } }); return; }
    if (!user.is_admin) { navigate("/"); return; }
    load();
  }, [user, loading, navigate, load]);

  const toggleRead = async (m) => {
    const { data } = await http.patch(`/contact/messages/${m.message_id}`, { read: !m.read });
    setMsgs((list) => list.map((x) => (x.message_id === m.message_id ? data : x)));
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete the message from ${m.name}?`)) return;
    await http.delete(`/contact/messages/${m.message_id}`);
    setMsgs((list) => list.filter((x) => x.message_id !== m.message_id));
    toast.success("Message deleted");
  };

  const unread = msgs.filter((m) => !m.read).length;
  const shown = filter === "unread" ? msgs.filter((m) => !m.read) : msgs;

  return (
    <>
      <Seo title="Messages" path="/admin/messages" />
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Contact messages</h1>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="admin-unread-count">{unread} unread · {msgs.length} total</p>
          </div>
          <div className="flex gap-2">
            {["all", "unread"].map((f) => (
              <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`}>
                {f === "all" ? "All" : "Unread"}
              </Button>
            ))}
          </div>
        </div>

        {fetching ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>
        ) : !shown.length ? (
          <div className="mt-8 rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground" data-testid="admin-messages-empty">
            <Inbox className="mx-auto h-8 w-8" /><p className="mt-3 text-sm">No messages here yet.</p>
          </div>
        ) : (
          <ul className="mt-8 space-y-4" data-testid="admin-messages-list">
            {shown.map((m) => <MessageCard key={m.message_id} m={m} onToggle={toggleRead} onDelete={remove} />)}
          </ul>
        )}
      </div>
    </>
  );
}

function MessageCard({ m, onToggle, onDelete }) {
  return (
    <li className={`rounded-2xl border bg-card p-5 ${m.read ? "border-border" : "border-primary/40 shadow-sm"}`} data-testid={`admin-message-${m.message_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {!m.read && <span className="h-2 w-2 rounded-full bg-primary" />}
            <h2 className="font-semibold">{m.name}</h2>
            <Badge variant="secondary">{m.topic_label}</Badge>
            {m.email_status === "failed" && (
              <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> Email not delivered</Badge>
            )}
          </div>
          <a href={`mailto:${m.email}`} className="text-sm text-muted-foreground hover:text-primary">{m.email}</a>
        </div>
        <span className="text-xs text-muted-foreground">{fmt(m.created_at)}</span>
      </div>
      <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed">{m.message}</p>
      <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
        <Button asChild size="sm" variant="outline" className="gap-1.5">
          <a href={`mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.topic_label}`)}`} data-testid={`admin-reply-${m.message_id}`}><Reply className="h-4 w-4" /> Reply</a>
        </Button>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onToggle(m)} data-testid={`admin-toggle-read-${m.message_id}`}>
          {m.read ? <><Mail className="h-4 w-4" /> Mark unread</> : <><MailOpen className="h-4 w-4" /> Mark read</>}
        </Button>
        <Button size="sm" variant="ghost" className="gap-1.5 text-destructive" onClick={() => onDelete(m)} data-testid={`admin-delete-${m.message_id}`}>
          <Trash2 className="h-4 w-4" /> Delete
        </Button>
      </div>
    </li>
  );
}
